import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Lightbulb, Code2, Rocket, HeartHandshake, Star, Download, Smartphone, Gamepad2, NotebookPen, Ghost, Zap, Timer, Lock, ShieldOff, Gift, Sparkles, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { MatrixRain } from "../components/MatrixRain";
import { GhostLogo, Wordmark } from "../components/Logo";
import { api } from "../lib/api";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";

const ICONS = { gamepad: Gamepad2, notebook: NotebookPen, ghost: Ghost, zap: Zap, timer: Timer, lock: Lock };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Counter = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1800;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.floor(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return <span ref={ref}>{n.toLocaleString("it-IT")}{suffix}</span>;
};

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`relative max-w-7xl mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

export default function Landing() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("tutte");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/apps?category=${filter}`).then(({ data }) => setApps(data)).catch(() => {});
  }, [filter]);

  const ctaLink = user ? "/dashboard" : "/auth";

  return (
    <div className="bg-[#080A0C] text-gray-100 overflow-x-hidden">
      <Navbar />

      {/* ============ HERO ============ */}
      <div ref={heroRef} className="relative min-h-screen flex items-center terminal-grid scanlines">
        <MatrixRain opacity={0.3} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#080A0C]/40 to-[#080A0C]" />
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-[#00FF66] border border-[#00FF66]/30 rounded-full px-4 py-1.5 mb-8 bg-[#00FF66]/5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" /> {t.heroBadge}
            </motion.div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white">
              <span className="reveal-line"><span>{t.h1l1}</span></span>
              <span className="reveal-line"><span>{t.h1l2a}<span className="neon-text">{t.h1l2b}</span>.</span></span>
              <span className="reveal-line"><span>{t.h1l3}</span></span>
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-7 max-w-xl text-base md:text-lg text-gray-400 leading-relaxed">
              {t.heroP1}<span className="text-gray-200">{t.heroP2}</span>{t.heroP3}
            </motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-4 font-mono text-xs text-[#00FF66]/80 caret">
              {t.terminal}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }} className="mt-10 flex flex-wrap gap-4">
              <Link to={ctaLink} data-testid="hero-submit-idea-btn" className="btn-neon px-8 py-4 text-sm inline-flex items-center gap-2">
                {t.ctaIdea} <ArrowRight size={15} />
              </Link>
              <a href="#apps" data-testid="hero-explore-apps-btn" className="btn-ghost-outline px-8 py-4 text-sm inline-flex items-center">{t.ctaExplore}</a>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {[
                { key: "idee", label: t.statIdeas, value: stats?.ideas ?? 137 },
                { key: "app", label: t.statApps, value: stats?.apps ?? 6 },
                { key: "donazioni", label: t.statDonations, value: stats?.donations ?? 18250 },
              ].map((s) => (
                <div key={s.key} data-testid={`hero-stat-${s.key}`}>
                  <div className="font-display text-2xl sm:text-3xl font-bold text-white"><Counter value={s.value} /></div>
                  <div className="font-mono text-[10px] tracking-widest text-gray-500 mt-1 uppercase">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div style={{ y: ghostY }} className="hidden lg:flex justify-center">
            <div className="ghost-float relative">
              <div className="absolute inset-0 blur-[90px] bg-[#00FF66]/20 rounded-full scale-125" />
              <GhostLogo size={260} />
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ============ MARQUEE ============ */}
      <div className="relative border-y border-[#1F2937]/70 bg-[#0D1117] py-5 overflow-hidden" data-testid="marquee-section">
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center shrink-0">
              {t.marquee.map((m) => (
                <span key={m} className="flex items-center font-display font-bold text-xl sm:text-2xl tracking-wide text-gray-600 mx-8">
                  {m} <Sparkles size={16} className="ml-16 text-[#00FF66]/60" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ============ COME FUNZIONA ============ */}
      <Section id="come-funziona" className="py-28 sm:py-36">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <p className="font-mono text-xs tracking-[0.3em] text-[#00FF66] mb-4">{t.howTag}</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white max-w-2xl">{t.howT1}<span className="neon-text">{t.howTNeon}</span></h2>
        </motion.div>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.steps.map((step, i) => {
            const Icon = [Lightbulb, Code2, Rocket, HeartHandshake][i];
            const num = `0${i + 1}`;
            return (
              <motion.div key={num} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                data-testid={`how-step-${num}`}
                className="glass-card glow-border rounded-2xl p-7 relative overflow-hidden">
                <span className="absolute top-4 right-5 font-mono text-4xl font-bold text-[#1F2937]/80">{num}</span>
                <Icon size={28} className="text-[#00FF66] mb-5" />
                <h3 className="font-display font-semibold text-lg text-white mb-3">{step.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* ============ APP PUBBLICATE ============ */}
      <Section id="apps" className="py-16 pb-28 sm:pb-36">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-[#00FF66] mb-4">{t.showTag}</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white">{t.showT1}<span className="neon-text">{t.showTNeon}</span></h2>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["tutte", "giochi", "produttivita", "utility"].map((val) => (
              <button key={val} data-testid={`filter-${val}-btn`} onClick={() => setFilter(val)}
                className={`font-mono text-xs px-4 py-2 rounded-full border transition-all ${filter === val ? "bg-[#00FF66] text-[#080A0C] border-[#00FF66] font-bold" : "border-[#1F2937] text-gray-400 hover:border-[#00FF66]/50 hover:text-[#00FF66]"}`}>
                {t.filters[val]}
              </button>
            ))}
          </div>
        </motion.div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="apps-grid">
          {apps.map((app, i) => {
            const Icon = ICONS[app.icon] || Smartphone;
            return (
              <motion.div key={app.id} custom={i % 3} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
                className="glass-card glow-border rounded-2xl group">
                <Link to={`/app/${app.id}`} data-testid={`app-card-${app.name.toLowerCase()}`} className="block p-6">
                <div className="flex items-start justify-between mb-5">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center border" style={{ borderColor: `${app.color}55`, background: `${app.color}10` }}>
                    <Icon size={26} style={{ color: app.color }} />
                  </div>
                  <div className="flex items-center gap-1 font-mono text-xs text-gray-300">
                    <Star size={13} className="text-[#F59E0B] fill-[#F59E0B]" /> {app.rating}
                  </div>
                </div>
                <h3 className="font-display font-semibold text-lg text-white group-hover:text-[#00FF66] transition-colors">{app.name}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed min-h-[42px]">{lang === "it" ? app.tagline : (app.tagline_en || app.tagline)}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {app.platforms.map((p) => (
                    <span key={p} className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-[#1F2937] text-gray-400">{p}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#1F2937]/70">
                  <span className="flex items-center gap-1.5 font-mono text-xs text-gray-500"><Download size={12} /> {app.downloads.toLocaleString(lang === "it" ? "it-IT" : "en-GB")}</span>
                  <span className="flex items-center gap-1.5 font-mono text-xs text-[#00FF66]"><Gift size={12} /> €{app.donations.toLocaleString(lang === "it" ? "it-IT" : "en-GB")} {t.donated}</span>
                </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* ============ DONAZIONI ============ */}
      <Section id="donazioni" className="pb-28 sm:pb-36">
        <div className="glass-card rounded-3xl p-10 sm:p-16 relative overflow-hidden terminal-grid">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#00FF66]/10 blur-[100px] rounded-full" />
          <div className="grid lg:grid-cols-2 gap-12 items-center relative">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
              <p className="font-mono text-xs tracking-[0.3em] text-[#00FF66] mb-4">{t.donTag}</p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white">{t.donT1} <br /><span className="neon-text">{t.donTNeon}</span></h2>
              <p className="mt-6 text-gray-400 leading-relaxed max-w-lg">
                {t.donP1}<span className="text-gray-200">{t.donP2}</span>{t.donP3}
              </p>
            </motion.div>
            <div className="space-y-4">
              {t.donFeatures.map((f, i) => {
                const FIcon = [ShieldOff, Gift, Rocket][i];
                return (
                  <motion.div key={f.title} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
                    className="flex gap-4 items-start bg-[#080A0C]/60 border border-[#1F2937] rounded-xl p-5">
                    <FIcon size={20} className="text-[#00FF66] mt-0.5 shrink-0" />
                    <div>
                      <h3 className="font-display font-semibold text-white text-base">{f.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* ============ CTA FINALE ============ */}
      <Section className="pb-28 text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <div className="flex justify-center mb-8"><div className="ghost-float"><GhostLogo size={80} /></div></div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white">{t.ctaT1}<span className="neon-text">{t.ctaTNeon}</span>{t.ctaT2}</h2>
          <p className="mt-4 text-gray-400 max-w-xl mx-auto">{t.ctaP}</p>
          <Link to={ctaLink} data-testid="cta-register-btn" className="btn-neon px-10 py-4 text-sm inline-flex items-center gap-2 mt-9">
            {t.ctaBtn} <ArrowRight size={15} />
          </Link>
        </motion.div>
      </Section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-[#1F2937]/70 py-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <Wordmark size={28} />
          <div className="flex flex-col sm:items-end items-center gap-2">
            <div className="flex items-center gap-5">
              <Link to="/privacy" data-testid="footer-privacy-link" className="font-mono text-[11px] text-gray-500 hover:text-[#00FF66] tracking-wider transition-colors">{t.footerPrivacy}</Link>
              <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="font-mono text-[11px] text-gray-500 hover:text-[#00FF66] tracking-wider transition-colors">{t.footerDisclaimer}</Link>
            </div>
            <p className="font-mono text-[11px] text-gray-600 tracking-wider">{t.footer}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
